/**
 * Группы экрана «Планы» и выбор партий — одним местом для таблицы и страницы.
 *
 * Спека: `docs/specs/2026-09-15-plans-screen-design.md`. Ключ группы обязан
 * совпадать с тем, каким сервер считает `group_count`
 * (`app/core/modules/plan/plan/service.py::plan_totals`): иначе «704 группы» в
 * сводке спорили бы с числом заголовков в таблице.
 */

import type { SmartKey } from "@/lib/crmSmartButtons";
import { nf0 } from "@/lib/format";
import { MAX_LIST } from "@/lib/productNav";
import type { PlanRow } from "@/lib/providers";

/** Группа — поставщик, QR-папка и плановая дата; без папки поставщик и дата. */
export type PlanGroup = {
  key: string;
  qrId: number | null;
  /** Плановая дата группы — и у группы с папкой: партии разных дат разошлись. */
  date: string;
  supplier: string;
  /** Номер поставщика — под меню поставщика; `null` у партий без поставщика. */
  supplierId: number | null;
  plans: PlanRow[];
  /** Итог ВСЕЙ папки мимо отбора экрана — «сколько уже в заказе»; `null` без папки. */
  folder: FolderTotals | null;
};

/** План и заказано по всей QR-папке — тем же правилом, что карточка `/bqd/:id`. */
export type FolderTotals = { plan: number; ordered: number };

/**
 * Имя поставщика — по правилу кабинета: ключ поиска, иначе короткое имя. Этим
 * порядком его печатает CRM и кабинет Ozon; список отбора, заголовок группы и
 * колонка обязаны звать поставщика одним именем.
 */
export function supplierName(row: PlanRow): string {
  return row.supplier_key || row.supplier_short || "без поставщика";
}

/** Метка строки: короткий знак, тон из токенов темы и подсказка словами. */
export type Mark = { text: string; tone: "ok" | "err" | "muted"; title: string };

/**
 * Техкарта партии — файл, привязанный к ней в CRM («Тех Карты - m-74734
 * ms-38634.jpg»). Два состояния: есть и нет. «Есть» — ссылка на архив своей
 * ручки `/api/plans/techcard` (разметка 25.09.26 15:28 п.3).
 */
export function cardMark(row: PlanRow): Mark {
  return row.has_card
    ? { text: "есть", tone: "ok", title: "скачать техкарту партии с подписью, как в CRM" }
    : { text: "нет", tone: "err", title: "техкарты нет — фабрике нечего шить" };
}

/**
 * Архив техкарт — своя ручка, не CRM (`mpStockSupplyPlanDateFileDownload`). Папка
 * сильнее списка — как у `exportHref`.
 */
export function techcardHref({ ids, qr }: { ids?: number[] | undefined; qr?: number | undefined }): string {
  const params = new URLSearchParams();
  if (qr) params.set("qr", String(qr));
  else params.set("ids", (ids ?? []).join(","));
  return `/api/plans/techcard?${params}`;
}

/** Существующая QR-папка — строка `GET /api/plans/qr/folders` и `folder` сухого
 * прогона `POST /api/plans/qr` с `qr_id` (разметка 28.09.26 11:27 п.1). */
export type QrFolder = {
  qr_id: number;
  category_id: number;
  status_id: number;
  status: string | null;
  supplier_id: number | null;
  plan_date: string | null;
  created_at: string | null;
  plans: number;
  orders: number;
  /** Оплачено платежами папки, юани; `null` — оплаченных нет. */
  paid_amount: number | null;
};

/** `sBarcodeQrData_status`: 1 «Активный» — свой справочник, не статус партии. */
const FOLDER_ACTIVE = 1;

const дм = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;

/** Строка меню: «QR 812 · на 05.10 · 3 партии · Оплачен · ¥ 40 000». */
export function qrFolderLabel(f: QrFolder): string {
  const parts = [`QR ${f.qr_id}`];
  if (f.plan_date) parts.push(`на ${дм(f.plan_date)}`);
  parts.push(`${f.plans} ${plural(f.plans, ["партия", "партии", "партий"])}`);
  if (f.status) parts.push(f.status);
  if (f.paid_amount) parts.push(`¥ ${nf0.format(f.paid_amount)}`);
  return parts.join(" · ");
}

/**
 * Предупреждение в подтверждении (решение владельца 28.09.26): дописывать в
 * оплаченную, отправленную или заказанную папку МОЖНО, но закупщик видит, чем
 * это кончится. Пустой список — папка «Активный» без заказов и оплат.
 */
export function qrFolderWarnings(f: QrFolder): string[] {
  const out: string[] = [];
  if (f.paid_amount)
    out.push(`папка оплачена ¥ ${nf0.format(f.paid_amount)} — сумма плана вырастет, оплата станет неполной`);
  if (f.orders > 0)
    out.push(`к папке привязан${f.orders > 1 ? "о" : ""} ${f.orders} ${plural(f.orders, ["заказ", "заказа", "заказов"])} фабрики — новые партии в ${f.orders > 1 ? "них" : "нём"} не появятся`);
  if (f.status_id !== FOLDER_ACTIVE && f.status) out.push(`статус папки — «${f.status}»`);
  return out;
}

/**
 * Оплата группы словами (разметка 25.09.26 15:28 п.2): «оплачено ¥ 12 345» либо
 * «не оплачено». Платёж в CRM висит на QR-ПАПКЕ, поэтому метка стоит только на
 * группе, а не в строке партии (п.1), и ответ берётся с партии, у которой папка
 * есть. `null` — у группы нет папки, и вопроса нет: в ОТБОРЕ «не оплачено» такие
 * партии всё равно есть — «что CRM знает» и «кому ещё не платили» разные вопросы.
 */
export function groupPaidMark(group: PlanGroup): Mark | null {
  const withQr = group.plans.find((plan) => plan.qr_id !== null);
  if (!withQr) return null;
  if (!withQr.is_paid)
    return { text: "не оплачено", tone: "err", title: "на QR-папке оплаченного платежа нет" };
  const sum = withQr.paid_amount;
  return {
    text: sum === null ? "оплачено" : `оплачено ¥ ${nf0.format(sum)}`,
    tone: "ok",
    title: "сумма оплаченных платежей QR-папки, юани",
  };
}

/**
 * Сумма группы по плану, юани (разметка 25.09.26 15:55 п.2): рядом с «оплачено
 * ¥ N» — во что заказ обходится по расчёту. Складываются `amount` партий
 * (строки DECIMAL); `noPrice` — строк плана без цены, сумма без них занижена.
 */
export function groupAmount(group: PlanGroup): { sum: number; noPrice: number } {
  let sum = 0;
  let noPrice = 0;
  for (const plan of group.plans) {
    sum += Number(plan.amount) || 0;
    noPrice += plan.no_price_lines;
  }
  return { sum, noPrice };
}

/**
 * Моделей в группе —сколько разных фасонов уходит фабрике (владелец 25.09.26:
 * «1000 штук платья — это 20 моделей»). Партия без модели — своим товаром, чтобы
 * не пропасть из счёта.
 */
export function groupModels(group: PlanGroup): number {
  return new Set(group.plans.map((plan) => (plan.model_id !== null ? `m${plan.model_id}` : `p${plan.product_id}`))).size;
}

/**
 * Ключ группы (замечания владельца 15.09.26 и 23.09.26): поставщик, QR-папка,
 * если она задана, и плановая дата — ВСЕГДА. Один поставщик разворачивается на
 * столько строк, сколько у него дат, и папка, собранная на две даты, тоже: её
 * партии уезжают фабрике разными днями, то есть это два заказа. Поставщик в
 * ключе и у папки: папка с партиями двух поставщиков (одна такая в CRM на
 * 15.09.26) честно встаёт двумя группами.
 */
export function groupKey(row: PlanRow): string {
  const supplier = row.supplier_id ?? 0;
  const date = row.plan_date.slice(0, 10);
  return row.qr_id ? `${supplier}|q${row.qr_id}|d${date}` : `${supplier}|d${date}`;
}

/** Группы в порядке первого появления: список уже отсортирован свежей датой вниз. */
export function groupPlans(
  rows: PlanRow[],
  folders: Record<string, FolderTotals> = {},
): PlanGroup[] {
  const groups = new Map<string, PlanGroup>();
  for (const row of rows) {
    const key = groupKey(row);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        qrId: row.qr_id,
        date: row.plan_date.slice(0, 10),
        supplier: supplierName(row),
        supplierId: row.supplier_id,
        plans: [],
        folder: row.qr_id ? (folders[String(row.qr_id)] ?? null) : null,
      };
      groups.set(key, group);
    }
    group.plans.push(row);
  }
  return [...groups.values()];
}

/**
 * Товары отмеченных партий — без повторов и в порядке таблицы: группы сверху
 * вниз, партии внутри группы. Партий у товара бывает несколько, карточка одна.
 * Длиннее `MAX_LIST` список не режется молча — это решает кнопка, называя
 * причину.
 */
export function pickedProducts(groups: PlanGroup[], picked: ReadonlySet<number>): number[] {
  const out: number[] = [];
  const seen = new Set<number>();
  for (const group of groups) {
    for (const plan of group.plans) {
      if (!picked.has(plan.id) || seen.has(plan.product_id)) continue;
      seen.add(plan.product_id);
      out.push(plan.product_id);
    }
  }
  return out;
}

/** Пропустит ли адрес такой список. */
export function listFits(products: number[]): boolean {
  return products.length > 0 && products.length <= MAX_LIST;
}

/**
 * Номера смарт-меню CRM по отмеченным партиям (спека …-plans-export-smart,
 * часть 1): партии, их строки плана, папки, оплаты папок и поставщики — как
 * `joinUniq` CRM: без повторов, пустые выброшены, порядок — порядок `rows`
 * (страница отдаёт их порядком отметок). Две партии одной папки дают ОДИН
 * номер папки: CRM по повтору открыла бы ту же папку дважды.
 */
export function smartIdsOfPlans(rows: PlanRow[]): Partial<Record<SmartKey, number[]>> {
  const out = {
    mpStockSupplyPlanDateID: [] as number[],
    mpStockSupplyPlanID: [] as number[],
    barcodeQrDataID: [] as number[],
    cryptoID: [] as number[],
    supplySupplierID: [] as number[],
  };
  const было = new Map<keyof typeof out, Set<number>>();
  const положить = (key: keyof typeof out, id: number | null) => {
    if (!id) return;
    const set = было.get(key) ?? new Set<number>();
    было.set(key, set);
    if (set.has(id)) return;
    set.add(id);
    out[key].push(id);
  };
  for (const row of rows) {
    положить("mpStockSupplyPlanDateID", row.id);
    for (const id of row.plan_line_ids) положить("mpStockSupplyPlanID", id);
    положить("barcodeQrDataID", row.qr_id);
    for (const id of row.crypto_ids) положить("cryptoID", id);
    положить("supplySupplierID", row.supplier_id);
  }
  return out;
}

/** Потолок партий в один файл — тот же, что у сервера (`export/data.MAX_PLANS`). */
export const MAX_EXPORT_PLANS = 200;
/** Потолок `GET /api/plans?plan_ids=` — список едет в адресе и в запросе. */
export const MAX_PLAN_IDS = 500;

/**
 * Адрес файла заказа фабрике — своя ручка `/api/plans/export`, не CRM. Тот же
 * источник, поэтому кука сессии едет сама и хватает `<a href download>`. Папка
 * сильнее списка: значок группы качает ВСЮ папку, какие бы партии ни были
 * отмечены.
 */
export function exportHref(
  kind: "list" | "size",
  lang: "ru" | "cn",
  { ids, qr }: { ids?: number[] | undefined; qr?: number | undefined },
): string {
  const params = new URLSearchParams({ kind, lang });
  if (qr) params.set("qr", String(qr));
  else params.set("ids", (ids ?? []).join(","));
  return `/api/plans/export?${params}`;
}

/** Картинка QR-папки — своя генерация (часть 3 спеки), не страница CRM. */
export function qrPngHref(qrId: number): string {
  return `/api/plans/qr/${qrId}.png`;
}

/** «Активный»: только такой партии CRM проставит номер папки. */
const STATUS_ACTIVE = 1;

/** Потолок партий в одну папку — тот же, что у сервера (`qr.MAX_PLANS`). */
export const MAX_QR_PLANS = 200;

/** Несколько номеров строкой, но не простынёй: подсказка кнопки не резиновая. */
function few(values: string[]): string {
  return values.slice(0, 5).join(", ") + (values.length > 5 ? ` и ещё ${values.length - 5}` : "");
}

/**
 * Почему отмеченным партиям нельзя завести QR-папку — первая причина словами,
 * пусто — можно. Те же правила, что проверяет сервер (`qr.problems`), и кнопка
 * называет причину ДО нажатия: пересчёт CRM пишет номер папки только пустой
 * активной партии, а папка едет одной фабрике.
 */
export function qrProblem(plans: PlanRow[], blocked: string | null): string {
  if (blocked) return blocked;
  if (plans.length === 0) return "не выбрано ни одной партии";
  if (plans.length > MAX_QR_PLANS) return `не больше ${MAX_QR_PLANS} партий в одну папку`;
  const withQr = plans.filter((plan) => plan.qr_id);
  if (withQr.length > 0)
    return `уже в QR-папке: ${few(withQr.map((plan) => `${plan.id} (QR ${plan.qr_id})`))}`;
  const inactive = plans.filter((plan) => plan.status_id !== STATUS_ACTIVE);
  if (inactive.length > 0)
    return `не активны: ${few(inactive.map((plan) => String(plan.id)))} — папку получает только активная партия`;
  const suppliers = new Set(plans.map((plan) => plan.supplier_id));
  if (suppliers.has(null)) return "есть партии без поставщика";
  if (suppliers.size > 1) return "разные поставщики — папка едет одной фабрике";
  return "";
}

/** Потолок групп одного прохода «QR + WeChat»: сотня заказов — почти семь часов очереди. */
export const MAX_BULK_GROUPS = 100;

/**
 * Проход «QR + WeChat» (просьба владельца 23.10.26, спека
 * `2026-10-23-plans-bulk-qr-wechat`): отмеченные партии по группам таблицы.
 * Группа без папки получает свою — на ОТМЕЧЕННЫЕ партии, теми же правилами
 * `qrProblem`; группа с папкой отправляет её. Папка на две даты — две группы,
 * но один заказ: в отправке она один раз.
 */
export type BulkPlan = {
  create: { group: PlanGroup; planIds: number[] }[];
  send: { qrId: number; planIds: number[] }[];
  skip: { group: PlanGroup; why: string }[];
  /** Почему кнопка закрыта целиком; пусто — открыта. */
  why: string;
};

export function bulkPlan(groups: PlanGroup[], picked: ReadonlySet<number>, qrBlocked: string | null): BulkPlan {
  const plan: BulkPlan = { create: [], send: [], skip: [], why: "" };
  const touched = groups.flatMap((group) => {
    const plans = group.plans.filter((row) => picked.has(row.id));
    return plans.length ? [{ group, plans }] : [];
  });
  if (touched.length > MAX_BULK_GROUPS) return { ...plan, why: `не больше ${MAX_BULK_GROUPS} групп за раз` };
  for (const { group, plans } of touched) {
    const planIds = plans.map((row) => row.id);
    if (group.qrId) {
      const was = plan.send.find((s) => s.qrId === group.qrId);
      if (was) was.planIds.push(...planIds);
      else plan.send.push({ qrId: group.qrId, planIds });
      continue;
    }
    const why = qrProblem(plans, qrBlocked);
    if (why) plan.skip.push({ group, why });
    else plan.create.push({ group, planIds });
  }
  if (!plan.create.length && !plan.send.length) plan.why = plan.skip[0]?.why ?? "не выбрано ни одной партии";
  return plan;
}

/** Текст подтверждения: всё, что будет записано и отправлено, — до записи. */
export function bulkConfirm(plan: BulkPlan, ratePerHour: number): string {
  const total = plan.create.length + plan.send.length;
  const заказов = `${total} ${plural(total, ["заказ", "заказа", "заказов"])}`;
  const папки = plan.send.map((s) => `bqd-${s.qrId}`).join(", ");
  const day = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
  const lines = plan.create.length
    ? [
        `Создать ${plan.create.length} ${plural(plan.create.length, ["QR-папку", "QR-папки", "QR-папок"])} и отправить в WeChat ${заказов}?`,
        "",
        "Новые папки: " +
          plan.create
            .map(
              (c) =>
                `${c.group.supplier} (${c.planIds.length} ${plural(c.planIds.length, ["партия", "партии", "партий"])}, ${day(c.group.date)})`,
            )
            .join(", "),
      ]
    : [`Отправить в WeChat ${заказов}: ${папки}?`, ""];
  if (plan.create.length && plan.send.length) lines.push(`Уже в папке: ${папки}`);
  if (plan.skip.length) lines.push(`Пропущу: ${plan.skip.map((s) => `${s.group.supplier} — ${s.why}`).join("; ")}`);
  lines.push("Каждому — QR, Excel на китайском, текст. Уже отправленные папки пропущу.");
  if (total > ratePerHour)
    lines.push(
      `WeChat шлёт не больше ${ratePerHour} в час — последние уйдут примерно через ${Math.floor((total - 1) / ratePerHour)} ч, метка «отправляется…».`,
    );
  return lines.join("\n");
}

/** «1 партия · 3 партии · 12 партий». */
export function plural(n: number, forms: readonly [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}
